import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown, Filter, Mail, Phone, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SalesTaxRatePanel, { leadBuyerAddress } from '@/components/admin/SalesTaxRatePanel';
import { groupItemsByDateSection } from '@/utils/dateSections';
import { US_STATES } from '@/constants/usStates';
import { adminDialogPanel, adminOverlay } from '@/components/admin/adminUi';
import { getLeadTypeLabel, LEAD_TYPE_LABELS } from '@/constants/adminVocabulary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const PAYMENT_TYPES = [
  { value: 'cash', label: 'Cash Purchase' },
  { value: 'financing', label: 'Financing' },
];

const CONVERT_LEAD_TYPE_FILTERS = [
  { value: 'trade_in', label: getLeadTypeLabel('trade_in') },
  { value: 'financing', label: getLeadTypeLabel('financing') },
  { value: 'prequalification', label: getLeadTypeLabel('prequalification') },
  { value: 'contact', label: getLeadTypeLabel('contact') },
  { value: 'vehicle_inquiry', label: getLeadTypeLabel('vehicle_inquiry') },
];

export const CONVERT_LEAD_TYPE_LABELS = LEAD_TYPE_LABELS;

const CONVERT_LEAD_TYPE_COLORS = {
  contact: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  vehicle_inquiry: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
  financing: 'bg-green-500/10 text-green-400 border-green-500/30',
  trade_in: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
  prequalification: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
};

const formatUsd = (n) => {
  const num = Number(n);
  if (Number.isNaN(num)) return '—';
  return `$${num.toLocaleString()}`;
};

const fieldLabelCls = "block text-base font-['Manrope'] font-medium text-white/80 mb-1.5";

const DEFAULT_CASH_FEES = {
  documentary_fee: '358',
  title_and_registration_fee: '316',
  ert_fee: '35',
  sales_tax_percent: '8.5',
  charge_ert_fee: true,
  tax_override: false,
  tax_override_reason: '',
};

const computeLocalCashFees = ({ salePrice, documentaryFee, titleFee, ertFee, taxPercent, chargeErtFee }) => {
  const price = Math.max(0, parseInt(salePrice, 10) || 0);
  const doc = Math.max(0, parseFloat(documentaryFee) || 0);
  const title = Math.max(0, parseFloat(titleFee) || 0);
  const ert = Math.max(0, parseFloat(ertFee) || 0);
  const rate = Math.max(0, parseFloat(taxPercent) || 0) / 100;
  const salesTax = Math.round(price * rate);
  const totalDue = Math.round(price + doc + title + ert + salesTax);
  return {
    sale_price: price,
    documentary_fee: Math.round(doc),
    title_and_registration_fee: Math.round(title),
    ert_fee: Math.round(ert),
    sales_tax_rate: rate,
    sales_tax: salesTax,
    charge_ert_fee: Boolean(chargeErtFee),
    total_due: totalDue,
    cash_amount: totalDue,
  };
};

export function askingPriceForDeal(vehicle) {
  if (!vehicle || vehicle.price_not_decided) return '';
  const n = Number(vehicle.price);
  if (!Number.isFinite(n) || n <= 0) return '';
  return String(Math.round(n));
}

function mergePrefillVehicle(list, initialVehicle) {
  const vehicles = Array.isArray(list) ? list : [];
  if (!initialVehicle?.id) return vehicles;
  if (vehicles.some((item) => item.id === initialVehicle.id)) return vehicles;
  return [initialVehicle, ...vehicles];
}

function CashFeeEditor({ form, setForm, token, testIdPrefix = 'cash' }) {
  const salePrice = parseInt(form.sale_price, 10);
  const hasPrice = form.sale_price !== '' && !Number.isNaN(salePrice) && salePrice >= 0;
  const preview = hasPrice
    ? computeLocalCashFees({
      salePrice: form.sale_price,
      documentaryFee: form.documentary_fee,
      titleFee: form.title_and_registration_fee,
      ertFee: form.ert_fee,
      taxPercent: form.sales_tax_percent,
      chargeErtFee: form.charge_ert_fee,
    })
    : null;

  const feeInputCls =
    "w-28 bg-white/5 border border-white/10 text-white text-sm px-2 py-1.5 font-['Manrope'] text-right tabular-nums focus:outline-none focus:border-blue-500/50";

  if (!hasPrice) {
    return (
      <div className="space-y-3 border border-white/10 bg-white/[0.03] p-4" data-testid={`${testIdPrefix}-fee-breakdown`}>
        <p className="text-[11px] font-['Unbounded'] text-white/50 uppercase tracking-wider">Deal fees</p>
        <p className="text-xs font-['Manrope'] text-white/40">Enter a sale price to load fees.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 border border-white/10 bg-white/[0.03] p-4" data-testid={`${testIdPrefix}-fee-breakdown`}>
      <p className="text-[11px] font-['Unbounded'] text-white/50 uppercase tracking-wider">Deal fees</p>
      <div className="space-y-2">
        <div className="flex justify-between items-center gap-3 text-base font-['Manrope']">
          <span className="text-white/80 font-medium">Documentary Fee</span>
          <input
            type="number"
            min="0"
            step="1"
            value={form.documentary_fee}
            onChange={(e) => setForm((p) => ({ ...p, documentary_fee: e.target.value }))}
            className={feeInputCls}
            data-testid={`${testIdPrefix}-doc-fee`}
          />
        </div>
        <SalesTaxRatePanel
          token={token}
          state={form.customer_state}
          zip={form.customer_zip}
          city={form.customer_city}
          salesTaxPercent={form.sales_tax_percent}
          onRateChange={(sales_tax_percent) => setForm((p) => ({ ...p, sales_tax_percent }))}
          override={form.tax_override}
          onOverrideChange={(tax_override) => setForm((p) => ({ ...p, tax_override }))}
          overrideReason={form.tax_override_reason}
          onOverrideReasonChange={(tax_override_reason) => setForm((p) => ({ ...p, tax_override_reason }))}
          testIdPrefix={testIdPrefix}
          rowClassName="text-base font-['Manrope']"
          labelClassName="text-white/80 font-medium"
          overrideLabelClassName="text-base font-['Manrope'] font-medium text-white/80"
        />
        <div className="flex justify-end">
          <span className="text-white/40 text-xs tabular-nums">{formatUsd(preview.sales_tax)}</span>
        </div>
        <div className="flex justify-between items-center gap-3 text-base font-['Manrope']">
          <span className="text-white/80 font-medium">Title and Registration Fee</span>
          <input
            type="number"
            min="0"
            step="1"
            value={form.title_and_registration_fee}
            onChange={(e) => setForm((p) => ({ ...p, title_and_registration_fee: e.target.value }))}
            className={feeInputCls}
            data-testid={`${testIdPrefix}-title-reg-fee`}
          />
        </div>
        <div className="flex justify-between items-center gap-3 text-base font-['Manrope']">
          <span className="text-white/80 font-medium">ERT Fee</span>
          <input
            type="number"
            min="0"
            step="1"
            value={form.ert_fee}
            onChange={(e) => setForm((p) => ({ ...p, ert_fee: e.target.value, charge_ert_fee: Number(e.target.value) > 0 }))}
            className={feeInputCls}
            data-testid={`${testIdPrefix}-ert-fee`}
          />
        </div>
      </div>

      <div className="border-t border-white/10 pt-3 space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-base font-['Manrope'] font-semibold text-white">Total Due</span>
          <span className="text-base font-['Manrope'] font-bold text-emerald-400 tabular-nums" data-testid={`${testIdPrefix}-total-due`}>
            {formatUsd(preview.total_due)}
          </span>
        </div>
        <div>
          <label className={fieldLabelCls}>Cash Amount</label>
          <input
            type="text"
            value={formatUsd(preview.cash_amount)}
            readOnly
            data-testid={`${testIdPrefix}-cash-amount`}
            className="w-full bg-white/[0.03] border border-white/10 text-white/50 text-sm px-3 py-2.5 font-['Manrope'] cursor-not-allowed"
          />
          <p className="mt-1 text-[11px] font-['Manrope'] text-white/35">
            Matches Total Due (sale price + fees + tax). Not editable.
          </p>
        </div>
      </div>
    </div>
  );
}

export function ConvertLeadPicker({
  leads,
  selectedId,
  onSelect,
  onClose,
  title = 'Choose a Lead',
  showTypeFilter = true,
  allowedTypes,
  testId = 'convert-lead-picker',
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilters, setTypeFilters] = useState(
    () => CONVERT_LEAD_TYPE_FILTERS.map((t) => t.value),
  );
  const [filterOpen, setFilterOpen] = useState(false);

  const toggleType = (value) => {
    setTypeFilters((prev) => (
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    ));
  };

  const filteredLeads = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const lockedTypes = Array.isArray(allowedTypes) && allowedTypes.length ? allowedTypes : null;
    const activeTypes = lockedTypes || (typeFilters.length ? typeFilters : CONVERT_LEAD_TYPE_FILTERS.map((t) => t.value));
    return (leads || []).filter((lead) => {
      if (!activeTypes.includes(lead.type)) return false;
      if (!query) return true;
      return (
        String(lead.name || '').toLowerCase().includes(query)
        || String(lead.email || '').toLowerCase().includes(query)
        || String(lead.phone || '').toLowerCase().includes(query)
      );
    });
  }, [leads, searchTerm, typeFilters, allowedTypes]);

  const groupedLeads = useMemo(
    () => groupItemsByDateSection(filteredLeads, (lead) => lead.created_at),
    [filteredLeads],
  );

  return (
    <div className={adminOverlay('z-[70]')} data-testid={testId}>
      <div className={`${adminDialogPanel('max-w-2xl')} flex flex-col`}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-lg font-['Unbounded'] font-bold text-white">{title}</h3>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white text-xl">&times;</button>
        </div>

        <div className="p-4 border-b border-white/10 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search name, email, or phone"
              data-testid={`${testId}-search`}
              className="w-full bg-white/5 border border-white/10 text-white text-sm pl-9 pr-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
            />
          </div>
          {showTypeFilter && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setFilterOpen((open) => !open)}
                data-testid={`${testId}-type-filter`}
                className="h-full flex items-center gap-2 px-3 py-2.5 bg-white/5 border border-white/10 text-white text-sm font-['Manrope'] hover:bg-white/10"
              >
                <Filter className="w-4 h-4 text-white/50" />
                Types
                <ChevronDown className={`w-3.5 h-3.5 text-white/40 transition-transform ${filterOpen ? 'rotate-180' : ''}`} />
              </button>
              {filterOpen && (
                <div className="absolute right-0 mt-2 z-10 w-56 bg-[#111] border border-white/10 p-3 space-y-2">
                  {CONVERT_LEAD_TYPE_FILTERS.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={typeFilters.includes(opt.value)}
                        onChange={() => toggleType(opt.value)}
                        className="accent-blue-500"
                      />
                      <span className="text-sm font-['Manrope'] text-white/80">{opt.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {groupedLeads.length === 0 ? (
            <p className="p-8 text-center text-sm font-['Manrope'] text-white/40">No leads match your search or filters.</p>
          ) : groupedLeads.map((section) => (
            <div key={section.key}>
              <div className="flex items-center gap-3 px-4 py-2.5 bg-white/[0.045] sticky top-0">
                <span className="text-[11px] font-['Unbounded'] font-semibold uppercase tracking-[0.16em] text-white/75 whitespace-nowrap">
                  {section.label}
                </span>
                <span className="h-px flex-1 bg-gradient-to-r from-white/20 to-transparent" />
                <span className="text-[10px] font-['Manrope'] text-white/40 tabular-nums">{section.items.length}</span>
              </div>
              {section.items.map((lead) => {
                const selected = lead.id === selectedId;
                return (
                  <button
                    type="button"
                    key={lead.id}
                    onClick={() => onSelect(lead)}
                    data-testid={`convert-pick-lead-${lead.id}`}
                    className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.04] transition-colors ${selected ? 'bg-blue-500/10' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-['Manrope'] font-semibold text-white truncate">{lead.name || 'Unnamed lead'}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] font-['Manrope'] text-white/45">
                          {lead.email && (
                            <span className="inline-flex items-center gap-1 truncate">
                              <Mail className="w-3 h-3" />
                              {lead.email}
                            </span>
                          )}
                          {lead.phone && (
                            <span className="inline-flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {lead.phone}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`shrink-0 px-2.5 py-1 text-[11px] font-['Manrope'] border ${CONVERT_LEAD_TYPE_COLORS[lead.type] || CONVERT_LEAD_TYPE_COLORS.contact}`}>
                        {CONVERT_LEAD_TYPE_LABELS[lead.type] || lead.type}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CreateDealModal({
  token,
  onClose,
  onSuccess,
  initialVehicleId = '',
  initialSalePrice = '',
  initialVehicle = null,
}) {
  const headers = { Authorization: `Bearer ${token}` };
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    customer_city: '',
    customer_state: '',
    customer_zip: '',
    vehicle_id: initialVehicleId || '',
    payment_type: 'cash',
    sale_price: initialSalePrice != null && initialSalePrice !== '' ? String(initialSalePrice) : '',
    down_payment: '',
    trade_in_value: '',
    trade_in_condition: '',
    trade_in_notes: '',
    ...DEFAULT_CASH_FEES,
  });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/vehicles`)
      .then((res) => setVehicles(mergePrefillVehicle(res.data, initialVehicle)))
      .catch(() => toast.error('Failed to load vehicles'))
      .finally(() => setLoading(false));
    // Prefill vehicle is snapshotted when the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialVehicleId]);

  const handleVehicleChange = (vehicleId) => {
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    setForm((prev) => ({
      ...prev,
      vehicle_id: vehicleId,
      sale_price: vehicle?.price ? String(vehicle.price) : prev.sale_price,
    }));
  };

  const handlePaymentTypeChange = (payment_type) => {
    setForm((prev) => ({
      ...prev,
      payment_type,
      charge_ert_fee: payment_type === 'cash' ? prev.charge_ert_fee : false,
    }));
  };

  const handleSalePriceChange = (sale_price) => {
    setForm((prev) => ({
      ...prev,
      sale_price,
    }));
  };

  const handleSubmit = async () => {
    if (!form.customer_name.trim() || !form.vehicle_id) {
      toast.error('Customer name and vehicle are required');
      return;
    }
    if (form.tax_override && !form.tax_override_reason.trim()) {
      toast.error('A reason is required for a sales-tax override');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        vehicle_id: form.vehicle_id,
        payment_type: form.payment_type,
        ...(form.customer_email.trim() && { customer_email: form.customer_email.trim() }),
        ...(form.customer_phone.trim() && { customer_phone: form.customer_phone.trim() }),
        ...(form.customer_address.trim() && { customer_address: form.customer_address.trim() }),
        ...(form.customer_city.trim() && { customer_city: form.customer_city.trim() }),
        ...(form.customer_state.trim() && { customer_state: form.customer_state.trim() }),
        ...(form.customer_zip.trim() && { customer_zip: form.customer_zip.trim() }),
        ...(form.sale_price && { sale_price: parseInt(form.sale_price, 10) }),
        ...(form.payment_type === 'financing' && form.down_payment && { down_payment: parseInt(form.down_payment, 10) }),
        charge_ert_fee: Number(form.ert_fee) > 0,
        documentary_fee: parseInt(form.documentary_fee, 10) || 0,
        title_and_registration_fee: parseInt(form.title_and_registration_fee, 10) || 0,
        ert_fee: parseInt(form.ert_fee, 10) || 0,
        sales_tax_rate: parseFloat(form.sales_tax_percent) || 0,
        ...(form.tax_override && form.tax_override_reason.trim() && {
          sales_tax_override_reason: form.tax_override_reason.trim(),
        }),
        ...(form.trade_in_value && { trade_in_value: parseInt(form.trade_in_value, 10) }),
        ...(form.trade_in_condition && { trade_in_condition: form.trade_in_condition }),
        ...(form.trade_in_notes && { trade_in_notes: form.trade_in_notes }),
      };
      const res = await axios.post(`${BACKEND_URL}/api/admin/deals/create`, payload, { headers });
      onSuccess(res.data.deal_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create deal');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={adminOverlay('z-[60]')} data-testid="create-deal-modal">
      <div className={adminDialogPanel('max-w-lg')}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h2 className="text-lg font-['Unbounded'] font-bold text-white">Create Deal</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white text-xl">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          {loading ? (
            <p className="text-white/40 font-['Manrope'] text-sm text-center py-4">Loading vehicles...</p>
          ) : (
            <>
              <p className="text-xs font-['Manrope'] text-white/40">
                Create a deal without linking to a lead. Use cash purchase for buyers paying in full.
              </p>

              <div>
                <label className={fieldLabelCls}>Customer Name *</label>
                <input
                  type="text"
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  placeholder="John Smith"
                  data-testid="create-customer-name"
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={fieldLabelCls}>Email</label>
                  <input
                    type="email"
                    value={form.customer_email}
                    onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                    placeholder="john@email.com"
                    data-testid="create-customer-email"
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                  />
                </div>
                <div>
                  <label className={fieldLabelCls}>Phone</label>
                  <input
                    type="tel"
                    value={form.customer_phone}
                    onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    data-testid="create-customer-phone"
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                  />
                </div>
              </div>

              <div>
                <label className={fieldLabelCls}>Address</label>
                <input
                  type="text"
                  value={form.customer_address}
                  onChange={(e) => setForm({ ...form, customer_address: e.target.value })}
                  placeholder="123 Main St"
                  data-testid="create-customer-address"
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={fieldLabelCls}>City</label>
                  <input
                    type="text"
                    value={form.customer_city}
                    onChange={(e) => setForm({ ...form, customer_city: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                  />
                </div>
                <div>
                  <label className={fieldLabelCls}>State</label>
                  <input
                    type="text"
                    value={form.customer_state}
                    onChange={(e) => setForm({ ...form, customer_state: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                  />
                </div>
                <div>
                  <label className={fieldLabelCls}>ZIP</label>
                  <input
                    type="text"
                    value={form.customer_zip}
                    onChange={(e) => setForm({ ...form, customer_zip: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                  />
                </div>
              </div>

              <div>
                <label className={fieldLabelCls}>Purchase Type *</label>
                <select
                  value={form.payment_type}
                  onChange={(e) => handlePaymentTypeChange(e.target.value)}
                  data-testid="create-payment-type"
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900"
                >
                  {PAYMENT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={fieldLabelCls}>Select Vehicle *</label>
                <select
                  value={form.vehicle_id}
                  onChange={(e) => handleVehicleChange(e.target.value)}
                  data-testid="create-vehicle-select"
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900"
                >
                  <option value="">Choose a vehicle...</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.year} {v.make} {v.model} - ${v.price?.toLocaleString()} ({v.vin})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={fieldLabelCls}>Sale Price</label>
                <input
                  type="number"
                  value={form.sale_price}
                  onChange={(e) => handleSalePriceChange(e.target.value)}
                  placeholder="28000"
                  data-testid="create-sale-price"
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                />
              </div>

              {form.payment_type === 'financing' && (
                <div>
                  <label className={fieldLabelCls}>Down Payment</label>
                  <input
                    type="number"
                    value={form.down_payment}
                    onChange={(e) => setForm({ ...form, down_payment: e.target.value })}
                    placeholder="5000"
                    data-testid="create-down-payment"
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                  />
                </div>
              )}
              <CashFeeEditor form={form} setForm={setForm} token={token} testIdPrefix="create" />

              {form.payment_type === 'cash' && (
                <p className="text-xs font-['Manrope'] text-emerald-400/80">
                  Cash deals start at Ready to Close — no lender approval required.
                </p>
              )}
            </>
          )}
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope'] hover:bg-white/5 transition-colors">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting || loading}
            data-testid="create-deal-submit-btn"
            className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-['Manrope'] font-semibold transition-colors disabled:opacity-50"
          >
            {submitting ? 'Creating...' : 'Create Deal'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ConvertLeadModal({
  token,
  onClose,
  onSuccess,
  initialVehicleId = '',
  initialSalePrice = '',
  initialVehicle = null,
}) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [leads, setLeads] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showLeadPicker, setShowLeadPicker] = useState(false);
  const [form, setForm] = useState({
    lead_id: '', vehicle_id: initialVehicleId || '', payment_type: 'financing',
    sale_price: initialSalePrice != null && initialSalePrice !== '' ? String(initialSalePrice) : '',
    down_payment: '',
    customer_email: '', customer_phone: '',
    customer_address: '', customer_address_line2: '',
    customer_city: '', customer_state: '', customer_zip: '',
    ...DEFAULT_CASH_FEES,
  });

  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/admin/leads`, { headers }),
      axios.get(`${BACKEND_URL}/api/vehicles`),
    ]).then(([leadsRes, vehiclesRes]) => {
      setLeads(leadsRes.data);
      setVehicles(mergePrefillVehicle(vehiclesRes.data, initialVehicle));
    }).catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));
    // Prefill vehicle is snapshotted when the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers, initialVehicleId]);

  const handleVehicleChange = (vehicleId) => {
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    setForm((prev) => ({
      ...prev,
      vehicle_id: vehicleId,
      sale_price: vehicle?.price ? String(vehicle.price) : prev.sale_price,
      down_payment: prev.payment_type === 'cash' && vehicle?.price ? String(vehicle.price) : prev.down_payment,
    }));
  };

  const handlePaymentTypeChange = (payment_type) => {
    setForm((prev) => ({
      ...prev,
      payment_type,
      down_payment: payment_type === 'cash' && prev.sale_price ? prev.sale_price : prev.down_payment,
    }));
  };

  const handleSalePriceChange = (sale_price) => {
    setForm((prev) => ({
      ...prev,
      sale_price,
      down_payment: prev.payment_type === 'cash' ? sale_price : prev.down_payment,
    }));
  };

  const selectedLead = leads.find((item) => item.id === form.lead_id);

  const handlePickLead = (lead) => {
    const addr = leadBuyerAddress(lead);
    setForm((prev) => ({
      ...prev,
      lead_id: lead.id,
      customer_email: lead.email || '',
      customer_phone: lead.phone || '',
      customer_address: '',
      customer_address_line2: '',
      customer_city: addr.city,
      customer_state: addr.state,
      customer_zip: addr.zip,
    }));
    setShowLeadPicker(false);
  };

  const handleSubmit = async () => {
    if (!form.lead_id || !form.vehicle_id) {
      toast.error('Please select a lead and vehicle');
      return;
    }
    if (selectedLead?.type === 'contact') {
      if (!form.customer_email.trim() || !form.customer_phone.trim()) {
        toast.error('Email and phone are required for contact leads');
        return;
      }
      if (!form.customer_address.trim() || !form.customer_city.trim() || !form.customer_state.trim() || !form.customer_zip.trim()) {
        toast.error('Address line 1, city, state, and ZIP are required for contact leads');
        return;
      }
    }
    if (form.tax_override && !form.tax_override_reason.trim()) {
      toast.error('A reason is required for a sales-tax override');
      return;
    }
    setSubmitting(true);
    try {
      const params = {
        lead_id: form.lead_id,
        vehicle_id: form.vehicle_id,
        payment_type: form.payment_type,
        ...(form.sale_price && { sale_price: parseInt(form.sale_price, 10) }),
        ...(form.payment_type === 'financing' && form.down_payment && { down_payment: parseInt(form.down_payment, 10) }),
        charge_ert_fee: Number(form.ert_fee) > 0,
        documentary_fee: parseInt(form.documentary_fee, 10) || 0,
        title_and_registration_fee: parseInt(form.title_and_registration_fee, 10) || 0,
        ert_fee: parseInt(form.ert_fee, 10) || 0,
        sales_tax_rate: parseFloat(form.sales_tax_percent) || 0,
        ...(form.tax_override && form.tax_override_reason.trim() && {
          sales_tax_override_reason: form.tax_override_reason.trim(),
        }),
        ...(form.customer_email.trim() && { customer_email: form.customer_email.trim() }),
        ...(form.customer_phone.trim() && { customer_phone: form.customer_phone.trim() }),
        ...(form.customer_address.trim() && { customer_address: form.customer_address.trim() }),
        ...(form.customer_address_line2.trim() && { customer_address_line2: form.customer_address_line2.trim() }),
        ...(form.customer_city.trim() && { customer_city: form.customer_city.trim() }),
        ...(form.customer_state.trim() && { customer_state: form.customer_state.trim() }),
        ...(form.customer_zip.trim() && { customer_zip: form.customer_zip.trim() }),
      };
      const res = await axios.post(`${BACKEND_URL}/api/admin/deals/convert-from-lead`, null, { params, headers });
      onSuccess(res.data.deal_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to convert lead');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <div className={adminOverlay('z-[60]')} data-testid="convert-lead-modal">
      <div className={adminDialogPanel('max-w-lg')}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h2 className="text-lg font-['Unbounded'] font-bold text-white">Convert Lead to Deal</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white text-xl">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          {loading ? (
            <p className="text-white/40 font-['Manrope'] text-sm text-center py-4">Loading leads and vehicles...</p>
          ) : (
            <>
              <div>
                <label className={fieldLabelCls}>Select Lead *</label>
                <button
                  type="button"
                  onClick={() => setShowLeadPicker(true)}
                  data-testid="convert-lead-select"
                  className="w-full flex items-center justify-between gap-3 bg-white/5 border border-white/10 text-sm px-3 py-2.5 font-['Manrope'] hover:bg-white/10 focus:outline-none focus:border-blue-500/50 text-left"
                >
                  {selectedLead ? (
                    <span className="min-w-0 truncate text-white">
                      {selectedLead.name || 'Selected lead'}
                      {' — '}
                      {CONVERT_LEAD_TYPE_LABELS[selectedLead.type] || selectedLead.type}
                    </span>
                  ) : (
                    <span className="text-white/45">Choose a Lead...</span>
                  )}
                  <ChevronDown className="w-4 h-4 text-white/35 shrink-0" />
                </button>
              </div>
              {selectedLead?.type === 'contact' && (
                <div className="space-y-3 border border-white/10 p-4" data-testid="convert-contact-customer-fields">
                  <p className="text-xs font-['Manrope'] text-white/50">
                    Contact leads need complete customer information before converting.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={fieldLabelCls}>Email *</label>
                      <input
                        type="email"
                        value={form.customer_email}
                        onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                        data-testid="convert-customer-email"
                        className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                      />
                    </div>
                    <div>
                      <label className={fieldLabelCls}>Phone *</label>
                      <input
                        type="tel"
                        value={form.customer_phone}
                        onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                        data-testid="convert-customer-phone"
                        className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={fieldLabelCls}>Customer Address Line 1 *</label>
                    <input
                      type="text"
                      value={form.customer_address}
                      onChange={(e) => setForm({ ...form, customer_address: e.target.value })}
                      data-testid="convert-customer-address"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                    />
                  </div>
                  <div>
                    <label className={fieldLabelCls}>Address Line 2</label>
                    <input
                      type="text"
                      value={form.customer_address_line2}
                      onChange={(e) => setForm({ ...form, customer_address_line2: e.target.value })}
                      data-testid="convert-customer-address-line2"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className={fieldLabelCls}>City *</label>
                      <input
                        type="text"
                        value={form.customer_city}
                        onChange={(e) => setForm({ ...form, customer_city: e.target.value })}
                        data-testid="convert-customer-city"
                        className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                      />
                    </div>
                    <div>
                      <label className={fieldLabelCls}>State *</label>
                      <select
                        value={form.customer_state}
                        onChange={(e) => setForm({ ...form, customer_state: e.target.value })}
                        data-testid="convert-customer-state"
                        className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900"
                      >
                        <option value="">Select...</option>
                        {US_STATES.map((st) => (
                          <option key={st} value={st}>{st}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={fieldLabelCls}>ZIP *</label>
                      <input
                        type="text"
                        value={form.customer_zip}
                        onChange={(e) => setForm({ ...form, customer_zip: e.target.value })}
                        data-testid="convert-customer-zip"
                        className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                      />
                    </div>
                  </div>
                </div>
              )}
              <div>
                <label className={fieldLabelCls}>Purchase Type *</label>
                <select
                  value={form.payment_type}
                  onChange={(e) => handlePaymentTypeChange(e.target.value)}
                  data-testid="convert-payment-type"
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900"
                >
                  {PAYMENT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={fieldLabelCls}>Select Vehicle *</label>
                <select
                  value={form.vehicle_id}
                  onChange={(e) => handleVehicleChange(e.target.value)}
                  data-testid="convert-vehicle-select"
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900"
                >
                  <option value="">Choose a vehicle...</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.year} {v.make} {v.model} - ${v.price?.toLocaleString()} ({v.vin})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={fieldLabelCls}>Sale Price</label>
                <input
                  type="number"
                  value={form.sale_price}
                  onChange={(e) => handleSalePriceChange(e.target.value)}
                  placeholder="28000"
                  data-testid="convert-sale-price"
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                />
              </div>
              {form.payment_type === 'financing' && (
                <div>
                  <label className={fieldLabelCls}>Down Payment</label>
                  <input
                    type="number"
                    value={form.down_payment}
                    onChange={(e) => setForm({ ...form, down_payment: e.target.value })}
                    placeholder="5000"
                    data-testid="convert-down-payment"
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                  />
                </div>
              )}
              <CashFeeEditor form={form} setForm={setForm} token={token} testIdPrefix="convert" />
              {form.payment_type === 'cash' && (
                <p className="text-xs font-['Manrope'] text-emerald-400/80">
                  Cash deals start at Ready to Close — no lender approval required.
                </p>
              )}
            </>
          )}
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope'] hover:bg-white/5 transition-colors">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={submitting || loading}
            data-testid="convert-submit-btn"
            className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold transition-colors disabled:opacity-50"
          >
            {submitting ? 'Creating...' : 'Create Deal'}
          </button>
        </div>
      </div>
    </div>
    {showLeadPicker && (
      <ConvertLeadPicker
        leads={leads}
        selectedId={form.lead_id}
        onSelect={handlePickLead}
        onClose={() => setShowLeadPicker(false)}
      />
    )}
    </>
  );
}
